import { useState } from 'react';
import { toast } from 'react-toastify';
import { find, upperCase } from 'lodash';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { GridWidget } from '../types';
import { useUserStrategies } from '../../../context/UserStrategies';
import { getLogo } from '../../../helpers/functions';
import { ColumnsChart } from './ColumnsChart';
import { COLORS } from '../../../constants/colors';
import { SearchAssetsInput } from './SearchAssetsInput';
import { SEARCH_ASSETS } from '../../../http/apollo-queries';
import { Loading } from '../../UI';
import { IAsset } from '../../../types/model';
import { useLabels } from '../../../hooks/useLabels';
import { useErrorHandler } from 'react-error-boundary';

interface HistoricalIndexProps extends GridWidget {
  options: {
    strategyId: string;
  };
}

const COLOR_VALUES = Object.values(COLORS);

const HistoricalIndex = ({
  width,
  options: { strategyId },
}: HistoricalIndexProps) => {
  const indexToLabel = useLabels();
  const { t } = useTranslation();
  const onError = useErrorHandler();

  const { strategies } = useUserStrategies();
  const [searchAssets, { loading: loadingSearch }] = useLazyQuery<{
    searchAssets: { items: IAsset[] };
  }>(SEARCH_ASSETS, {
    fetchPolicy: 'network-only',
    onError,
  });

  const strategy = find(strategies, function getRelevantStrategy(strat) {
    return strat.id === strategyId;
  });

  const strategyAssets: IAsset[] =
    strategy?.strategyAssets?.items?.map(({ asset }) => asset) || [];

  const [searchResults, setSearchResults] = useState(strategyAssets);

  function search(searchTerm: string | undefined) {
    if (!searchTerm) return setSearchResults(strategyAssets);

    searchAssets({
      variables: {
        term: searchTerm + '*',
      },
    })
      .then(({ data }) => {
        setSearchResults(data?.searchAssets.items || []);
      })
      .catch(() => {
        toast.error('Something went wrong, please try again later');
      });
  }

  return (
    <div className="flex flex-col h-full">
      <SearchAssetsInput onFinishTyping={search} loading={false} />
      {loadingSearch ? (
        <div className="w-full  flex justify-center">
          <Loading status={true} />
        </div>
      ) : (
        <div
          className={`grid grid-cols-${width} overflow-scroll scrollbar-hide`}
        >
          {searchResults.map(function renderAssetItem(asset, index) {
            return (
              <div
                key={asset.id + index}
                className="h-40 border-grey-10 border-b border-r dark:border-grey-70"
              >
                <div className="flex flex-col justify-between h-full py-5 px-4 ">
                  <div className="flex gap-4 items-center">
                    <img
                      className="w-8 h-8 object-cover"
                      src={getLogo(asset.type, asset.logo)}
                      alt=""
                    />
                    <div className="w-3/4">
                      <p className="font-semibold text-md flex flex-wrap truncate w-full">
                        {upperCase(asset.symbol)}
                      </p>
                      <span className="text-xxs text-grey-50">
                        {asset.name}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-14 justify-between">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-xl">
                        {asset.historicalIndex.toFixed(1)}
                      </p>
                      <p className="text-grey-50 text-sm font-medium mt-2">
                        {upperCase(indexToLabel[asset.historicalIndex])}
                      </p>
                    </div>
                    <div className="flex flex-col mr-4">
                      <p className="text-sm mb-1">
                        {t('widget.historicalIndex.chartTitle')}
                      </p>
                      <ColumnsChart
                        color={COLOR_VALUES[index % (COLOR_VALUES.length - 1)]}
                        index={asset.historicalIndex}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HistoricalIndex;
